import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Divider, Link, Paper, Stack, TextField, Typography } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Description from '@mui/icons-material/Description';
import { useAuth } from '../../context/AuthContext';
import { urlAdjunto, useMisTramites, useResponderTramite, useTramiteAdjuntos, useTramiteEventos } from '../../hooks/useTramites';
import AdjuntosInput from '../../components/AdjuntosInput';
import { EstadoTramiteChip } from '../../components/Chips';
import { formatBytes, formatDate, formatDateTime } from '../../utils/format';

const ORIGEN: Record<string, string> = { afiliado: 'Vos', gremio: 'El gremio', sistema: 'Sistema' };

export default function TramiteDetalle({ id, mostrarVolver }: { id: string; mostrarVolver: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const creado = (location.state as { creado?: string } | null)?.creado;
  const { ficha } = useAuth();
  const { data: tramites, isLoading } = useMisTramites();
  const { data: eventos, isLoading: cargandoEventos } = useTramiteEventos(id);
  const { data: adjuntos, isLoading: cargandoAdjuntos } = useTramiteAdjuntos(id);
  const responder = useResponderTramite();

  const [mensaje, setMensaje] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [errorArchivo, setErrorArchivo] = useState<string | null>(null);
  const [errorEnvio, setErrorEnvio] = useState<string | null>(null);
  const [errorAdjunto, setErrorAdjunto] = useState<string | null>(null);

  const tramite = (tramites ?? []).find((t) => t.id === id);

  if (isLoading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!tramite) {
    return (
      <Stack spacing={2}>
        {mostrarVolver && (
          <Box>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/tramites')}>
              Volver
            </Button>
          </Box>
        )}
        <Alert severity="warning">No encontramos ese trámite.</Alert>
      </Stack>
    );
  }

  const handleResponder = async () => {
    setErrorEnvio(null);
    if (mensaje.trim().length < 2) {
      setErrorEnvio('Escribí tu respuesta.');
      return;
    }
    try {
      await responder.mutateAsync({ afiliadoId: ficha!.id, tramiteId: id, mensaje: mensaje.trim(), files });
      setMensaje('');
      setFiles([]);
    } catch (e) {
      setErrorEnvio((e as Error).message);
    }
  };

  const abrirAdjunto = async (path: string) => {
    setErrorAdjunto(null);
    try {
      window.open(await urlAdjunto(path), '_blank', 'noopener');
    } catch (e) {
      setErrorAdjunto((e as Error).message);
    }
  };

  return (
    <Stack spacing={2}>
      {mostrarVolver && (
        <Box>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/tramites')}>
            Volver a mis trámites
          </Button>
        </Box>
      )}
      {creado && <Alert severity="success">Enviamos tu trámite {creado}. El gremio lo va a revisar.</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h6" component="h2">
            {tramite.tipo}
          </Typography>
          <EstadoTramiteChip estado={tramite.estado} />
        </Stack>
        <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: 1, columnGap: 3 }}>
          <Typography component="dt" color="text.secondary">
            Trámite
          </Typography>
          <Typography component="dd" fontWeight={600} sx={{ m: 0 }}>
            {tramite.codigo}
          </Typography>
          <Typography component="dt" color="text.secondary">
            Iniciado
          </Typography>
          <Typography component="dd" fontWeight={600} sx={{ m: 0 }}>
            {formatDate(tramite.created_at)}
          </Typography>
          <Typography component="dt" color="text.secondary">
            Detalle
          </Typography>
          <Typography component="dd" sx={{ m: 0, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
            {tramite.detalle}
          </Typography>
          <Typography component="dt" color="text.secondary">
            Adjuntos
          </Typography>
          <Box component="dd" sx={{ m: 0 }}>
            {cargandoAdjuntos && <Typography color="text.secondary">Cargando…</Typography>}
            {!cargandoAdjuntos && (adjuntos ?? []).length === 0 && <Typography>Sin adjuntos</Typography>}
            <Stack spacing={0.5}>
              {(adjuntos ?? []).map((a) => (
                <Link key={a.id} component="button" type="button" underline="hover" onClick={() => abrirAdjunto(a.path)} sx={{ textAlign: 'left', display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                  <Description fontSize="small" /> {a.nombre} {a.tamano ? `(${formatBytes(a.tamano)})` : ''}
                </Link>
              ))}
            </Stack>
            {errorAdjunto && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {errorAdjunto}
              </Alert>
            )}
          </Box>
        </Box>
      </Paper>

      {tramite.estado === 'falta_informacion' && (
        <Paper variant="outlined" sx={{ p: 2.5, borderColor: 'warning.main' }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            El gremio te pidió información
          </Typography>
          <TextField
            label="Tu respuesta"
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            inputProps={{ maxLength: 2000 }}
            sx={{ mb: 2 }}
          />
          <AdjuntosInput files={files} onChange={setFiles} error={errorArchivo} onError={setErrorArchivo} disabled={responder.isPending} />
          {errorEnvio && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorEnvio}
            </Alert>
          )}
          <Button variant="contained" sx={{ mt: 2 }} onClick={handleResponder} disabled={responder.isPending}>
            {responder.isPending ? 'Enviando…' : 'Enviar respuesta'}
          </Button>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Seguimiento
        </Typography>
        {cargandoEventos && <Typography color="text.secondary">Cargando…</Typography>}
        <Stack divider={<Divider />} spacing={1.5}>
          {(eventos ?? []).map((ev) => (
            <Box key={ev.id}>
              <Typography fontWeight={600} sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                {ev.mensaje}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {ORIGEN[ev.origen] ?? ev.origen} · {formatDateTime(ev.created_at)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
}
